import {Poi} from '../../react-app-env';
import React, {ReactNode} from 'react';
import ListViewGroup from '../listview/listviewgroup/ListViewGroup';
import {i18n} from '../../i18n/i18n';
import ListViewItem from '../listview/listviewitem/ListViewItem';
import {ifFalse, noop, openLink} from '../../utils/Utils';
import {PoiMetaData} from '../../services/MetaDataService';
import {canHandleTelLinks} from '../../utils/Device';
import ListViewSplitItem from '../listview/listviewitem/ListViewSplitItem';
import {DefaultItemDecorations} from '../listview/listviewitem/ListViewItem.types';
import {Events, EventType} from '../../events/Events';
import {PopupMenuViewArgs} from '../popupmenu/PopupMenuView';
import {Coordinates} from '../../types/device.types';
import {getViewportCoordinatesForEvent} from '../../utils/Viewport';
import {useReadonly} from '../context/ReadonlyContext';

export interface LinksGroupProps {
  poi: Poi;
  metaData: PoiMetaData | null;
}

const LinksGroup: React.FunctionComponent<LinksGroupProps> = ({ poi, metaData }) => {
  const readonly = useReadonly();
  const ifWriteAccess = ifFalse(readonly);

  /**
   * Copies the phone number to the clipboard and shows a confirmation message.
   */
  async function copyPhoneNumberToClipboard() {
    await navigator.clipboard.writeText(metaData?.phoneNumber!);
    Events.publish(EventType.COPY_TO_CLIPBOARD_NOTIFICATION, i18n().poiDetails.links.copyPhoneConfirmation);
  }

  /**
   * Copies the website link to the clipboard and shows a confirmation message.
   */
  async function copyWebsiteLinkToClipboard() {
    await navigator.clipboard.writeText(metaData?.website!);
    Events.publish(EventType.COPY_TO_CLIPBOARD_NOTIFICATION, i18n().poiDetails.links.copyWebsiteConfirmation);
  }

  /**
   * Copies a custom link to the clipboard and shows a confirmation message.
   */
  async function copyCustomLinkToClipboard(link: string) {
    await navigator.clipboard.writeText(link);
    Events.publish(EventType.COPY_TO_CLIPBOARD_NOTIFICATION, i18n().poiDetails.links.copyLinkConfirmation);
  }

  /**
   * Opens the phone dialer with the POI's phone number.
   */
  function dialPhoneNumber() {
    openLink('tel:' + metaData?.phoneNumber, '_self');
  }

  /**
   * Returns whether a custom link can be added (max 5 links).
   */
  function canAddCustomLink(): boolean {
    return poi.links.length < 5;
  }

  /**
   * Shows the popup menu for custom link options.
   *
   * @param {string} link Link URL
   * @param {number} index Index of the link
   * @param {object} origin Optional origin coordinates for the popup menu.
   */
  function showCustomLinkOptions(link: string, index: number, origin?: Coordinates) {
    Events.publish(EventType.POPUPMENU_OPEN, {
      origin: origin,
      node:   <React.Fragment>
                <ListViewItem title={i18n().poiDetails.links.customLinkOptions.copyToClipboard}
                              onClick={() => {
                                copyCustomLinkToClipboard(link);
                                Events.publish(EventType.POPUPMENU_CLOSE);
                              }}
                              iconCategory="fa-copy"/>
                <ListViewItem title={i18n().poiDetails.links.customLinkOptions.editLink}
                              onClick={noop}
                              iconCategory="fa-edit"/>
                <ListViewItem title={i18n().poiDetails.links.customLinkOptions.deleteLink}
                              onClick={noop}
                              iconCategory="fa-trash"/>
              </React.Fragment>
    } as PopupMenuViewArgs);
  }

  /**
   * Renders a custom link.
   *
   * @param {string} link The link URL.
   * @param {number} index The index of the link in the list.
   * @return {React.ReactNode}
   */
  function renderCustomLink(link: string, index: number): ReactNode {
    try {
      const url = new URL(link);

      const primaryButton = {
        title:        url.host,
        ariaLabel:    i18n().poiDetails.links.openLink + ': ' + url.host,
        iconCategory: 'fa-link',
        decoration:   DefaultItemDecorations.EXTERNAL_LINK,
        onClick:      () => {
          const url = new URL(link);
          openLink(url.href);
        }
      };

      const secondaryButton = {
        decoration: readonly ? DefaultItemDecorations.COPY : DefaultItemDecorations.POPUPMENU,
        onClick:    (event: React.UIEvent) => {
          if (readonly) {
            copyCustomLinkToClipboard(link);
          } else {
            showCustomLinkOptions(link, index, getViewportCoordinatesForEvent(event));
          }
        }
      };

      return <ListViewSplitItem
          key={link}
          primaryButton={primaryButton}
          secondaryButton={secondaryButton}
      />;
    } catch(ex) {
      console.warn('Invalid link for POI:', link, ex);
      return null;
    }
  }

  /**
   * Renders the website link if available.
   * @return {React.ReactNode}
   */
  function renderWebsiteLink(): ReactNode {
    const website = metaData?.website;
    if (!website) {
      return null;
    }

    try {
      const url = new URL(website);
      return <ListViewSplitItem
          key={website}
          primaryButton={{
            title:        url.host,
            ariaLabel:    i18n().poiDetails.links.openLink + ': ' + url.host,
            iconCategory: 'fa-globe',
            decoration:   DefaultItemDecorations.EXTERNAL_LINK,
            onClick:      () => onWebsiteLinkClicked()
          }}
          secondaryButton={{
            decoration: DefaultItemDecorations.COPY,
            onClick:    () => copyWebsiteLinkToClipboard()
          }}
      />;
    } catch(ex) {
      console.warn('Invalid website metadata for POI:', website, ex);
      return null;
    }
  }

  /**
   * Renders a phone link item, which either opens the dialer or copies the number to clipboard.
   *
   * @return {React.ReactNode} The rendered phone link item.
   */
  function renderPhoneLink(): ReactNode {
    const phoneNumber = metaData?.phoneNumber;
    if (!phoneNumber) {
      return null;
    }

    if (canHandleTelLinks()) {
      return <ListViewItem key={phoneNumber}
                           title={phoneNumber}
                           ariaLabel={i18n().poiDetails.links.callPhoneNumber + ': ' + phoneNumber}
                           iconCategory="fa-phone"
                           decoration={DefaultItemDecorations.EXTERNAL_LINK}
                           onClick={() => dialPhoneNumber()}/>;
    } else {
      return <ListViewItem key={phoneNumber}
                           title={phoneNumber}
                           ariaLabel={i18n().poiDetails.links.callPhoneNumber + ': ' + phoneNumber}
                           iconCategory="fa-phone"
                           decoration={DefaultItemDecorations.COPY}
                           onClick={() => copyPhoneNumberToClipboard()}/>;
    }
  }

  /**
   * Handles the click event for the website link.
   */
  function onWebsiteLinkClicked() {
    const link = metaData?.website;
    if (link) {
      const url = new URL(link);
      openLink(url.href);
    }
  }

  const listItems = [
    renderPhoneLink(),
    renderWebsiteLink(),
    ...poi.links.map((item, index) => renderCustomLink(item, index)),
    canAddCustomLink() && ifWriteAccess(
        <ListViewItem title={i18n().poiDetails.links.addLink}
                      iconCategory="fa-plus"
                      decoration={DefaultItemDecorations.EDIT}
                      onClick={() => noop()}/>
    )
  ].filter(listItem => !!listItem);

  return listItems.length === 0 ? null : (
      <ListViewGroup>
        {listItems}
      </ListViewGroup>
  );
};

export default LinksGroup;
